import { Link } from 'react-router-dom';
import { DataTableCustom } from '../../components/dataTable/DataTableCustom';
import {
	PencilSquareIcon,
	ListBulletIcon,
	WindowIcon,
	ChevronRightIcon,
	ChartBarIcon,
} from '@heroicons/react/20/solid';
import { urlFlag } from '../../common/urlBase';
import { formatDate } from '../../common/formatDateText';
import { DateRangeProgressBarCell } from '../../components/dateRangeProgressBar/DateRangeProgressBar';
import { CourseWeekDaysGrid } from './components/CourseWeekDaysGrid';
import { Tooltip, Zoom } from '@mui/material';
import { useSelector } from 'react-redux';
import { canManageCourses } from '../../constants/courseManage';

function classNames(...classes) {
	return classes.filter(Boolean).join(' ')
}

export const CourseTableView = ({
	rows,
	pageSizeOptions,
	handlePageChange,
	onEditCourse,
}) => {
	const { user } = useSelector((state) => state.user);
	const { typeUser } = user;

	const allowedViewListStudents = ['director', 'administrativo', 'control escolar', 'ventas', 'cobranza'];
	const userCanManageCourses = canManageCourses(typeUser);
	const disabledButtonStatus = ['completado', 'cancelado'];

	const columns = [
		{
			sortable: false,
			field: 'action',
			width: 200,
			headerName: '',
			renderCell: (params) => (
				<div className='flex justify-center gap-2'>
					<Tooltip title="Entrar al curso" TransitionComponent={Zoom} placement='top' arrow>
						{
							disabledButtonStatus.includes(params.row.status)
								? (
									<span
										className="flex justify-center border-gray-200 border-1 bg-gray-300 uppercase px-3 py-2 text-slate-700 rounded hover:bg-gray-300 cursor-not-allowed opacity-50"
									>
										<WindowIcon className="block h-5 w-5" aria-hidden="true" />
									</span>
								) : (
									<Link
										to={`/curso/${params.row.id}`}
										className="flex justify-center border-gray-200 border-1 bg-gray-100 uppercase px-3 py-2 text-slate-700 rounded hover:bg-gray-300"
									>
										<WindowIcon className="block h-5 w-5" aria-hidden="true" />
									</Link>
								)
						}



					</Tooltip>
					{
						allowedViewListStudents.includes(typeUser) && (
							<Tooltip title="Lista de estudiantes" TransitionComponent={Zoom} placement='top' arrow>
								<Link
									to={`/curso-estatus/${params.row.id}`}
									className="flex justify-center border-gray-200 border-1 bg-gray-100 uppercase px-3 py-2 text-slate-700 rounded hover:bg-gray-300"
								>
									<ListBulletIcon className="block h-5 w-5" aria-hidden="true" />
								</Link>
							</Tooltip>
						)
					}
					{
						userCanManageCourses && (
							<Tooltip title="Editar curso" TransitionComponent={Zoom} placement='top' arrow>
								{
									disabledButtonStatus.includes(params.row.status)
										? (
											<span
												className="flex justify-center border-gray-200 border-1 bg-gray-300 uppercase px-3 py-2 text-slate-700 rounded hover:bg-gray-300 cursor-not-allowed opacity-50"
											>
												<PencilSquareIcon className="block h-5 w-5" aria-hidden="true" />
											</span>
										) : (
											onEditCourse ? (
												<button
													type="button"
													onClick={() => onEditCourse(params.row.id)}
													className="flex justify-center border-gray-200 border-1 bg-gray-100 uppercase px-3 py-2 text-slate-700 rounded hover:bg-gray-300"
												>
													<PencilSquareIcon className="block h-5 w-5" aria-hidden="true" />
												</button>
											) : (
											<Link
												to={`/editar-curso/${params.row.id}`}
												className="flex justify-center border-gray-200 border-1 bg-gray-100 uppercase px-3 py-2 text-slate-700 rounded hover:bg-gray-300"
											>
												<PencilSquareIcon className="block h-5 w-5" aria-hidden="true" />
											</Link>
											)
										)
								}

							</Tooltip>
						)
					}
				</div>

			)
		},
		{
			sortable: false,
			field: 'identifier',
			width: 200,
			headerName: 'Identificador',
			renderCell: (params) => (
				<p className='uppercase text-slate-600 font-semibold' to={`/perfil/${params.row.idUser}`}>{params.value}</p>
			)
		},
		{
			field: 'status',
			headerName: 'Estatus',
			width: 200,
			renderCell: (params) => (
				<div
					className={classNames(
						params.value === 'abierto'
							? 'text-sky-700 bg-sky-100'
							: params.value === 'en curso' ? 'text-emerald-600 bg-emerald-100'
								: params.value === 'completado' ? 'text-indigo-600 bg-indigo-100'
									: params.value === 'cancelado' ? 'text-gray-600 bg-gray-100'
										: params.value === 'finalizado' ? 'text-rose-500 bg-rose-100' : null,
						'flex justify-center w-[130px] py-1 rounded-md uppercase font-bold'
					)}>
					<p className="text-[.8rem]">{params.value}</p>
				</div>
			)
		},
		{
			sortable: false,
			field: 'language',
			width: 150,
			headerName: 'Idioma',
			renderCell: (params) => (
				<div className='flex justify-start gap-2 uppercase'>
					{
						params.value && (
							<>
								<img className="w-[28px]" src={`${urlFlag}${params.row.path}`} alt="flag" />
								<p className='mt-1 text-md'>{params.value}</p>
							</>
						)
					}
				</div>
			)
		},
		{
			field: 'level',
			width: 130,
			headerName: 'Level',
			renderCell: (params) => (
				<p className='uppercase'>{params.value}</p>
			)
		},
		{
			field: 'totalStudents',
			headerName: 'Total Est.',
			width: 100,
			renderCell: (params) => (
				<div className='w-[100%] flex justify-start'>
					<p className='capitalize bg-slate-200 px-3 py-1 font-semibold rounded-md text-sm text-gray-700'>
						{params.value}
					</p>
				</div>
			)
		},
		{
			field: 'limitMembers',
			headerName: 'Límite Est.',
			width: 100,
			renderCell: (params) => (
				<div className='w-[100%] flex justify-start'>
					<p className='capitalize bg-slate-200 px-3 py-1 font-semibold rounded-md text-sm text-gray-700'>
						{params.value}
					</p>
				</div>
			),
		},
		{
			field: 'validPaymentDeadline',
			headerName: 'Pagado',
			width: 100,
			renderCell: (params) => (
				<div className='w-[100%] flex justify-start'>
					<p className='capitalize bg-emerald-100 px-3 py-1 font-semibold rounded-md text-sm text-emerald-600'>
						{params.value}
					</p>
				</div>
			)
		},
		{
			field: 'expiredPaymentDeadline',
			headerName: 'Vencido',
			width: 100,
			renderCell: (params) => (
				<div className='w-[100%] flex justify-start'>
					<p className='capitalize bg-rose-100 px-3 py-1 font-semibold rounded-md text-sm text-rose-500'>
						{params.value}
					</p>
				</div>
			)
		},
		{
			field: 'teacher',
			headerName: 'Profesor',
			width: 200,
			renderCell: (params) => (
				<p className='capitalize'>{params.value}</p>
			),
		},
		{
			sortable: false,
			field: 'fromDate',
			headerName: 'Duracion de curso',
			width: 450,
			renderCell: (params) => (
				<div className='flex items-center gap-2'>
					{
						params.row.fromDate && (
							<p className='w-[200px] text-center capitalize bg-slate-200 px-3 py-1 rounded-md text-sm text-gray-700 overflow-hidden'>
								{formatDate(params.value)}
							</p>
						)
					}
					{
						params.row.toDate && (
							<>
								<span className='w-4'><ChevronRightIcon /></span>
								<p className='w-[200px] text-center capitalize bg-slate-200 px-3 py-1 rounded-md text-sm text-gray-700 overflow-hidden'>
									{formatDate(params.row.toDate)}
								</p>
							</>

						)
					}
				</div>
			),
		},
		{
			sortable: false,
			headerName: '',
			width: 250,
			renderHeader: () => (
				<div className="flex items-center gap-2">
					<ChartBarIcon className="h-4 w-4 text-green-600" />
					<span className="text-sm font-semibold text-gray-700">Progreso del curso</span>
				</div>
			),
			renderCell: (params) => (
				<DateRangeProgressBarCell
					startDate={params.row.fromDate}
					endDate={params.row.toDate}
					label={`Progreso del curso: `}
				/>
			),
		},
		{
			sortable: false,
			field: 'hours',
			headerName: 'Horarios',
			width: 150,
			renderCell: (params) => (
				<div className='flex w-full items-center'>
					{
						Array.isArray(params.value) && params.value.length > 0
							? (
								<p className='w-full truncate rounded-md bg-slate-200 px-2 py-1.5 text-center text-sm font-semibold text-slate-700'>
									{`${params.value[0]?.time}${params.value.length > 1 ? ` - ${params.value[params.value.length - 1]?.time}` : ''}`}
								</p>
							)
							: (
								<p className='rounded-md bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500'>
									Sin horario
								</p>
							)
					}
				</div>
			),
		},
		{
			sortable: false,
			field: 'days',
			headerName: 'Días',
			width: 250,
			renderCell: (params) => (
				<div className="flex items-center py-1 min-w-0 w-full">
					<CourseWeekDaysGrid days={params.value} compact showCheck={false} />
				</div>
			),
		},
		{
			field: 'updatedBy',
			headerName: 'Actualizado por',
			width: 250,
			renderCell: (params) => (
				<p className='capitalize'>{params.value}</p>
			)
		},
		{
			sortable: false,
			field: 'updatedAt',
			headerName: 'Actualizado en',
			width: 220,
			renderCell: (params) => (
				<p className='capitalize w-[200px] text-center bg-slate-200 px-3 py-1 rounded-md text-sm text-gray-700'>{formatDate(params.value)}</p>
			)
		},
	];

	return (
		<div className="overflow-x-auto">
			<DataTableCustom
				rows={rows}
				columns={columns}
				pageSizeOptions={pageSizeOptions}
				onPageChange={handlePageChange}
			/>
		</div>
	)
}
